<template>
  <div class="modern-table-card">
    <!-- HEADER -->
    <div
      class="table-header d-flex flex-wrap align-items-center justify-content-between gap-3"
    >
      <div>
        <h3 class="table-title mb-1">{{ currentLang.title }}</h3>
      </div>
    </div>

    <!-- ERROR -->
    <div v-if="errorMessage" class="alert alert-danger mt-3">
      {{ errorMessage }}
    </div>

    <!-- LOADING -->
    <div v-if="loading" class="text-center py-5">
      {{ currentLang.loading }}
    </div>

    <!-- TABLE -->
    <div v-else class="table-responsive">
      <table class="table custom-table align-middle">
        <thead>
          <tr>
            <th>{{ currentLang.id }}</th>
            <th>{{ currentLang.created }}</th>

            <th>{{ currentLang.subtotal }}</th>
            <th>{{ currentLang.totalDiscount }}</th>
            <th>{{ currentLang.totalPrice }}</th>

            <th>{{ currentLang.status }}</th>

            <th>{{ currentLang.operating }}</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in orders" :key="item.id">
            <td>{{ item.id }}</td>
            <th>{{ formatDate(item.created_at) }}</th>

            <td>{{ formatPrice(item.subtotal) }}</td>
            <td>{{ formatPrice(item.total_discount) }}</td>
            <td>{{ formatPrice(item.total_price) }}</td>

            <td>
              <span class="status-badge" :class="item.status">
                {{
                  {
                    registered: currentLang.statusRegistered,
                    approved: currentLang.statusApproved,
                    rejected: currentLang.statusRejected,
                  }[item.status] || ""
                }}
              </span>
            </td>

            <td>
              <div class="d-flex gap-2">
                <button
                  class="action-btn edit"
                  @click="$router.push(`/fa/dashboard/orders/${item.id}/print`)"
                >
                  <i class="bi bi-printer"></i>
                </button>

                <button
                  class="action-btn cancel"
                  :disabled="item.status !== 'registered'"
                  @click="openStatusModal(item)"
                >
                  <i class="bi bi-x-circle"></i>
                </button>
              </div>
            </td>
          </tr>

          <tr v-if="orders.length === 0">
            <td colspan="9" class="text-center py-4">
              {{ currentLang.empty }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- PAGINATION -->
    <div v-if="totalPages > 1" class="pagination-wrapper mt-4">
      <ul class="pagination justify-content-center mb-0">
        <!-- prev -->
        <li class="page-item" :class="{ disabled: currentPage === 1 }">
          <button class="page-link" @click="changePage(currentPage - 1)">
            {{ currentLang.previous }}
          </button>
        </li>

        <!-- pages -->
        <li
          v-for="page in totalPages"
          :key="page"
          class="page-item"
          :class="{ active: currentPage === page }"
        >
          <button class="page-link" @click="changePage(page)">
            {{ page }}
          </button>
        </li>

        <!-- next -->
        <li class="page-item" :class="{ disabled: currentPage === totalPages }">
          <button class="page-link" @click="changePage(currentPage + 1)">
            {{ currentLang.next }}
          </button>
        </li>
      </ul>
    </div>
  </div>

  <!-- STATUS MODAL -->
  <div v-if="showStatusModal" class="modal-backdrop-custom">
    <div class="modal-box">
      <span class="mb-3">
        {{ currentLang.deleteConfirm }}
      </span>

      <div class="d-flex justify-content-end gap-2">
        <button class="btn btn-warning" @click="closeStatusModal">
          {{ currentLang.cancel }}
        </button>

        <button class="btn btn-danger" @click="rejectOrder">
          {{ currentLang.confirm }}
        </button>
      </div>
    </div>
  </div>
</template>




<script setup>
import { ref, onMounted, watch, computed } from "vue";
import api from "@/plugins/axios";
import { lang } from "@/plugins/lang";
import enText from "@/locales/en/orderPage";
import faText from "@/locales/fa/orderPage";
import { showLoading, hideLoading } from "@/stores/loading";

const translations = {
  en: enText,
  fa: faText,
};
const currentLang = computed(() => translations[lang.value]);

/* =========================
   STATES
========================= */

const orders = ref([]);

const loading = ref(false);

const errorMessage = ref("");

const search = ref("");

const currentPage = ref(1);

const totalPages = ref(1);

const showStatusModal = ref(false);

const selectedStatus = ref("");

const selectedOrder = ref(null);

const openStatusModal = (order) => {
  selectedOrder.value = order;

  selectedStatus.value = "";

  showStatusModal.value = true;
};

const closeStatusModal = () => {
  showStatusModal.value = false;

  selectedOrder.value = null;

  selectedStatus.value = "";
};

const formatDate = (date) => {
  if (!date) return "-";

  return new Date(date).toLocaleString("fa-IR");
};

const formatPrice = (price) => {
  if (!price) return "0";

  return Number(price).toLocaleString();
};

/* =========================
   API
========================= */

const rejectOrder = async () => {
  try {
    const res = await api.patch(
      `/client/orders/${selectedOrder.value.id}/rejected`
    );

    if (res.status === 200) {
      closeStatusModal();
      fetchOrders();
    }
  } catch (error) {
    alert(error?.response?.data?.message || "خطا در تغییر وضعیت سفارش");
  }
};

const fetchOrders = async () => {
  loading.value = true;
  showLoading();

  errorMessage.value = "";

  try {
    const response = await api.get("/client/orders", {
      params: {
        page: currentPage.value,
        search: search.value,
      },
    });

    if (response.status === 200) {
      orders.value = response.data.data.orders;

      totalPages.value = response.data.data.pagination.total_pages;
    } else {
      errorMessage.value = response.data.message || "خطا در دریافت اطلاعات";
    }
  } catch (error) {
    errorMessage.value =
      error?.response?.data?.message || "خطا در ارتباط با سرور";
  } finally {
    loading.value = false;
    hideLoading();
  }
};

/* =========================
   PAGINATION
========================= */

const changePage = (page) => {
  if (page < 1 || page > totalPages.value) {
    return;
  }

  currentPage.value = page;

  fetchOrders();
};

/* =========================
   SEARCH
========================= */

let timeout = null;

watch(search, () => {
  currentPage.value = 1;

  clearTimeout(timeout);

  timeout = setTimeout(() => {
    fetchOrders();
  }, 500);
});

onMounted(() => {
  fetchOrders();
});
</script>




<style scoped>
/* =========================
   TABLE STRUCTURE
========================= */


.action-btn.cancel {
  background-color: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.action-btn.cancel:hover:not(:disabled) {
  background-color: #fca5a5;
  color: #991b1b;
}

.action-btn.cancel:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}


.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}

.modal-box {
  background: #fff;
  padding: 24px;
  border-radius: 12px;
  width: 320px;
}

.custom-table {
  margin-top: 24px;
  border-collapse: separate;
  border-spacing: 0 14px; /* فاصله بین کارت ها */
}

.custom-table thead th {
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  color: #94a3b8;
  padding: 10px 16px;
}

/* =========================
   CARD ROW
========================= */

.custom-table tbody tr {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04), 0 8px 24px rgba(0, 0, 0, 0.04);
  transition: all 0.25s ease;
}

.custom-table tbody tr:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08), 0 4px 10px rgba(0, 0, 0, 0.05);
}

/* حذف خطوط جدول */

.custom-table tbody td {
  border: none !important;
  padding: 18px 16px;
}

/* گرد کردن کارت */

.custom-table tbody tr td:first-child {
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
}

.custom-table tbody tr td:last-child {
  border-top-left-radius: 16px;
  border-bottom-left-radius: 16px;
}

/* =========================
   AVATAR
========================= */

.avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  box-shadow: 0 4px 10px rgba(99, 102, 241, 0.35);
}

/* =========================
   BADGES
========================= */

.role-badge {
  background: #eef2ff;
  color: #4f46e5;
  padding: 6px 14px;
  border-radius: 40px;
  font-size: 12px;
  font-weight: 600;
}

.status-badge {
  padding: 6px 14px;
  border-radius: 40px;
  font-size: 12px;
  font-weight: 700;
}

.status-badge.approved {
  background: #dcfce7;
  color: #16a34a;
}

.status-badge.rejected {
  background: #fee2e2;
  color: #dc2626;
}

.status-badge.registered {
  background: #fee2e2;
  color: #fed049;
}

/* =========================
   ACTION BUTTONS
========================= */

.action-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #f1f5f9;
  transition: 0.2s;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.search-box {
  position: relative;
  width: 280px;
}

.search-box i {
  position: absolute;
  top: 50%;
  right: 18px;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 15px;
  pointer-events: none;
  transition: 0.25s;
}

.search-box input {
  width: 100%;
  height: 52px;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  background: #fff;

  /* فاصله متن از آیکون */
  padding-right: 48px;
  padding-left: 16px;

  font-size: 14px;
  color: #0f172a;

  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.04);

  transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;
}

/* placeholder */
.search-box input::placeholder {
  color: #94a3b8;
}

/* focus */
.search-box input:focus {
  border-color: #c7d2fe;

  box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1),
    0 10px 25px rgba(99, 102, 241, 0.08);

  transform: translateY(-1px);
}

/* hover */
.search-box:hover input {
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

/* icon color on focus */
.search-box:focus-within i {
  color: #6366f1;
}

/* =========================
   RESPONSIVE
========================= */

@media (max-width: 768px) {
  .search-box {
    width: 100%;
  }

  .search-box input {
    height: 48px;
    font-size: 13px;
  }
}

/* =========================
   RESPONSIVE
========================= */

@media (max-width: 768px) {
  .modern-table-card {
    padding: 16px;
    border-radius: 18px;
  }

  .table-header {
    flex-direction: column;
    align-items: flex-start !important;
  }

  .search-box {
    width: 100%;
  }

  .custom-table thead {
    display: none;
  }

  .custom-table,
  .custom-table tbody,
  .custom-table tr,
  .custom-table td {
    display: block;
    width: 100%;
  }

  .custom-table tbody tr {
    margin-bottom: 16px;
    padding: 14px;
    border-radius: 18px;
  }

  .custom-table tbody td {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 12px 0;
    text-align: left;
    border: none !important;
  }

  .custom-table tbody td::before {
    content: attr(data-label);
    font-size: 13px;
    font-weight: 700;
    color: #94a3b8;
  }

  .custom-table tbody tr td:first-child,
  .custom-table tbody tr td:last-child {
    border-radius: 0;
  }

  .avatar {
    width: 40px;
    height: 40px;
    font-size: 14px;
  }
}
/* =========================
   PAGINATION
========================= */
.pagination-wrapper {
  margin-top: 25px;
  display: flex;
  justify-content: center;
  direction: rtl;
}

.pagination {
  flex-direction: row-reverse;
}

.pagination .page-link {
  border-radius: 10px;
  margin: 0 4px;
  min-width: 38px;
  text-align: center;
}

.pagination .page-item.active .page-link {
  background-color: #6366f1;
  border-color: #6366f1;
  color: #fff;
}

.pagination .page-link:hover {
  background: #eef2ff;
}
</style>
